import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, BarChart3, Wallet, PiggyBank, CreditCard, ArrowRight, PlayCircle, FileSpreadsheet, ShieldCheck, TrendingUp, Layout } from 'lucide-react';
import { MetaPixel } from './components/MetaPixel';
import { PurchasePopup } from './components/PurchasePopup';
// Images
const IMAGES = {
  hero: 'https://cdn.magicpatterns.com/uploads/cWWbwsuRTvDFM7hZkfba3N/Diseno_sin_titulo_(7).png',
  planner: 'https://cdn.magicpatterns.com/uploads/fVaV3UyKxzZmnCvaiVnikC/planner.png',
  tutorial: 'https://cdn.magicpatterns.com/uploads/hqKvFut7oTwmVbz4AEPZMG/tutorial.png'
};
const CHECKOUT_URL = 'https://pay.hotmart.com/R103783715V';
export function App() {
  const scrollToPricing = () => {
    const element = document.getElementById('precio');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <PurchasePopup data-id="element-2" />

      {/* Hero Section */}
      <header className="relative bg-white overflow-hidden" data-id="element-3">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-white z-0" data-id="element-4" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 relative z-10" data-id="element-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-6">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-600 text-white text-sm font-bold mb-6" data-id="element-8">
                <FileSpreadsheet className="w-4 h-4" data-id="element-9" />
                Plantilla de Finanzas Personales
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6 leading-tight text-center" data-id="element-10">
                ¿Sentís que tu dinero no te alcanza y no sabés por qué?
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed text-center" data-id="element-11">
                No sos el único. La mayoría no tiene un sistema claro para
                organizar sus finanzas. Esta plantilla en Google Sheets te ayuda
                a ordenar ingresos, gastos, ahorros y deudas de forma simple y
                sin complicaciones.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-12">
                {['Entendé en qué se va tu dinero', 'Organizá tus deudas sin estrés', 'Empezá a ahorrar con claridad', 'Todo en un solo lugar'].map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2 + i * 0.1
              }} className="flex items-center gap-3 text-slate-700" data-id="element-13">
                    <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0" data-id="element-14" />
                    {item}
                  </motion.li>)}
              </ul>

              <button onClick={scrollToPricing} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-15">
                Empezar a ordenar mis finanzas
                <ArrowRight className="w-5 h-5" data-id="element-16" />
              </button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-17">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white bg-white" data-id="element-18">
                <img src={IMAGES.hero} alt="Vista previa de la plantilla de finanzas" className="w-full h-auto object-cover" data-id="element-19" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-teal-100 rounded-full blur-2xl -z-10" data-id="element-20" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-100 rounded-full blur-2xl -z-10" data-id="element-21" />
            </motion.div>
          </div>
        </div>
      </header>

      {/* Pain Validation */}
      <section className="py-20 bg-white" data-id="element-22">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-23">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-24">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-25">
              No es que ganes poco, es que falta claridad
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed" data-id="element-26">
              Es frustrante llegar a fin de mes y preguntarse "¿en qué se me fue
              la plata?". Pagar cuentas y cuotas sin sentir que avanzás genera
              ansiedad y estrés. El problema no son siempre tus ingresos, sino
              la falta de un sistema que te permita ver la foto completa de tu
              economía.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 bg-slate-50" data-id="element-27">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-28">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-29">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-30">
              Un sistema simple para ordenar tus finanzas
            </h2>
            <p className="text-lg text-slate-600" data-id="element-31">
              Diseñada para personas reales con gastos reales. Sin fórmulas
              complicadas ni términos técnicos. Solo ingresás tus datos y
              obtenés claridad inmediata.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-32">
            {[{
            icon: <Wallet className="w-8 h-8 text-teal-600" data-id="element-33" />,
            title: 'Registro de Ingresos',
            desc: 'Visualizá todo lo que entra a tu bolsillo en un solo lugar.'
          }, {
            icon: <CreditCard className="w-8 h-8 text-red-500" data-id="element-34" />,
            title: 'Control de Gastos',
            desc: 'Categorizá tus gastos para saber exactamente dónde recortar.'
          }, {
            icon: <PiggyBank className="w-8 h-8 text-emerald-500" data-id="element-35" />,
            title: 'Seguimiento de Ahorros',
            desc: 'Establecé metas y mirá cómo crecen tus ahorros mes a mes.'
          }, {
            icon: <TrendingUp className="w-8 h-8 text-orange-500" data-id="element-36" />,
            title: 'Gestión de Deudas',
            desc: 'Organizá montos, cuotas y visualizá tu progreso para salir de ellas.'
          }, {
            icon: <BarChart3 className="w-8 h-8 text-blue-500" data-id="element-37" />,
            title: 'Resúmenes Automáticos',
            desc: 'Gráficos claros que se actualizan solos al cargar tus datos.'
          }, {
            icon: <FileSpreadsheet className="w-8 h-8 text-purple-500" data-id="element-38" />,
            title: '100% Editable',
            desc: 'Usala en Google Sheets desde tu compu o celular. Es tuya para siempre.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow" data-id="element-39">
                <div className="bg-slate-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6" data-id="element-40">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-41">
                  {feature.title}
                </h3>
                <p className="text-slate-600" data-id="element-42">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-id="element-43">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-44">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-45">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-46">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-47">
                Recuperá la tranquilidad financiera
              </h2>
              <div className="space-y-6" data-id="element-48">
                {[{
                title: 'Dejá de adivinar',
                desc: 'Sabé exactamente si llegás a fin de mes antes de que sea tarde.'
              }, {
                title: 'Reducí el estrés',
                desc: 'Al tener todo anotado, tu mente descansa. No más cuentas mentales.'
              }, {
                title: 'Tomá mejores decisiones',
                desc: 'Decidí compras importantes basándote en datos reales, no en sensaciones.'
              }, {
                title: 'Proyectá a futuro',
                desc: 'Empezá a construir el hábito del ahorro paso a paso.'
              }].map((benefit, i) => <div key={i} className="flex gap-4" data-id="element-49">
                    <div className="flex-shrink-0 mt-1" data-id="element-50">
                      <ShieldCheck className="w-6 h-6 text-teal-500" data-id="element-51" />
                    </div>
                    <div data-id="element-52">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-53">
                        {benefit.title}
                      </h3>
                      <p className="text-slate-600" data-id="element-54">{benefit.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative hidden lg:block" data-id="element-55">
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-100 to-blue-50 rounded-3xl transform rotate-3" data-id="element-56"></div>
              <div className="relative bg-white p-8 rounded-3xl shadow-xl border border-slate-100" data-id="element-57">
                <div className="space-y-4" data-id="element-58">
                  <div className="h-4 bg-slate-100 rounded w-3/4" data-id="element-59"></div>
                  <div className="h-4 bg-slate-100 rounded w-1/2" data-id="element-60"></div>
                  <div className="h-32 bg-teal-50 rounded-xl w-full flex items-center justify-center" data-id="element-61">
                    <BarChart3 className="w-16 h-16 text-teal-200" data-id="element-62" />
                  </div>
                  <div className="h-4 bg-slate-100 rounded w-full" data-id="element-63"></div>
                  <div className="h-4 bg-slate-100 rounded w-5/6" data-id="element-64"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-20 bg-slate-900 text-white" data-id="element-65">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-66">
          <div className="text-center mb-16" data-id="element-67">
            <span className="text-teal-400 font-bold tracking-wider uppercase text-sm" data-id="element-68">
              Regalos Exclusivos
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2" data-id="element-69">
              Bonos incluidos con tu compra
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12" data-id="element-70">
            {/* Bonus 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700" data-id="element-71">
              <img src={IMAGES.planner} alt="Planner Financiero" className="w-full h-64 object-cover" data-id="element-72" />
              <div className="p-8" data-id="element-73">
                <div className="inline-block bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded mb-4" data-id="element-74">
                  BONUS #1
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-75">
                  Planner Financiero Imprimible
                </h3>
                <p className="text-slate-300" data-id="element-76">
                  Ideal para planificar tu mes en papel, anotar prioridades,
                  fechas de vencimiento y establecer tus metas financieras antes
                  de pasarlas a la planilla.
                </p>
              </div>
            </motion.div>

            {/* Bonus 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700" data-id="element-77">
              <img src={IMAGES.tutorial} alt="Video Tutorial" className="w-full h-64 object-cover" data-id="element-78" />
              <div className="p-8" data-id="element-79">
                <div className="inline-block bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded mb-4" data-id="element-80">
                  BONUS #2
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-81">
                  Video Tutorial Paso a Paso
                </h3>
                <p className="text-slate-300" data-id="element-82">
                  Te explico paso a paso cómo usar la plantilla desde cero para
                  que le saques el máximo provecho.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white" data-id="element-83">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-84">
          <div className="text-center mb-16" data-id="element-85">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" data-id="element-86">
              Cómo funciona
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-87">
            {[{
            step: '01',
            title: 'Accedé a la plantilla',
            desc: 'Recibís acceso inmediato a tu correo después de la compra.'
          }, {
            step: '02',
            title: 'Cargá tus datos',
            desc: 'Ingresá tus ingresos, gastos, ahorros y deudas en las celdas indicadas.'
          }, {
            step: '03',
            title: 'Visualizá tu situación',
            desc: 'Los gráficos y resúmenes se actualizan automáticamente.'
          }].map((item, i) => <div key={i} className="relative text-center p-6" data-id="element-88">
                <div className="text-6xl font-black text-slate-100 absolute top-0 left-1/2 -translate-x-1/2 -z-10" data-id="element-89">
                  {item.step}
                </div>
                <div className="bg-teal-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-teal-600 font-bold text-xl relative z-10" data-id="element-90">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-91">
                  {item.title}
                </h3>
                <p className="text-slate-600" data-id="element-92">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precio" className="py-20 bg-teal-50" data-id="element-93">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-94">
          <div className="max-w-lg mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-teal-100" data-id="element-95">
            <div className="bg-teal-600 p-6 text-center text-white" data-id="element-96">
              <h2 className="text-2xl font-bold" data-id="element-97">Oferta Especial</h2>
              <p className="text-teal-100" data-id="element-98">Acceso inmediato y de por vida</p>
            </div>

            <div className="p-8 md:p-12" data-id="element-99">
              <div className="text-center mb-8" data-id="element-100">
                <p className="text-slate-400 line-through text-lg mb-2" data-id="element-101">
                  Precio regular: $49.99
                </p>
                <div className="flex items-center justify-center gap-2" data-id="element-102">
                  <span className="text-5xl font-bold text-slate-900" data-id="element-103">
                    $11.99
                  </span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-bold" data-id="element-104">
                    -76%
                  </span>
                </div>
                <p className="text-slate-500 mt-2" data-id="element-105">Pago único</p>
              </div>

              <ul className="space-y-4 mb-10" data-id="element-106">
                {['Plantilla de Finanzas Personales (Google Sheets)', 'Gestión de Ingresos y Gastos', 'Control de Deudas y Ahorros', 'BONUS: Planner Financiero Imprimible', 'BONUS: Video Tutorial Paso a Paso', 'Acceso de por vida', 'Soporte por correo'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-700" data-id="element-107">
                    <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-108" />
                    <span className="text-sm md:text-base" data-id="element-109">{item}</span>
                  </li>)}
              </ul>

              <a href={CHECKOUT_URL} className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center font-bold py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 mb-4" data-id="element-110">
                Acceder ahora por $11.99
              </a>

              <div className="flex items-center justify-center gap-4 text-slate-400 text-sm" data-id="element-111">
                <div className="flex items-center gap-1" data-id="element-112">
                  <ShieldCheck className="w-4 h-4" data-id="element-113" />
                  Pago seguro
                </div>
                <div className="flex items-center gap-1" data-id="element-114">
                  <Layout className="w-4 h-4" data-id="element-115" />
                  Entrega inmediata
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white text-center" data-id="element-116">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-118">
            Empezá hoy a construir tu tranquilidad financiera
          </h2>
          <p className="text-xl text-slate-600 mb-10" data-id="element-119">
            No dejes pasar otro mes sin saber a dónde va tu dinero. Ordená tus
            finanzas hoy mismo.
          </p>
          <a href={CHECKOUT_URL} className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-10 py-5 rounded-xl text-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-1" data-id="element-120">
            Empezar hoy por $11.99
            <ArrowRight className="w-6 h-6" data-id="element-121" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 py-12 border-t border-slate-200" data-id="element-122">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm" data-id="element-123">
          <p data-id="element-124">
            © {new Date().getFullYear()} Plantilla de Finanzas Personales.
            Todos los derechos reservados.
          </p>
          <p className="mt-2" data-id="element-125">
            Este sitio no forma parte del sitio web de Facebook o Facebook Inc.
          </p>
        </div>
      </footer>
    </div>;
}